<template>
  <v-container class="py-8" style="max-width: 700px">
    <div class="d-flex align-center mb-6">
      <img src="../assets/logo.png" alt="Gigdriver" width="40" height="40" class="mr-3" />
      <h1 class="text-h5">Contract</h1>
    </div>

    <v-alert v-if="loadError" type="error" variant="tonal">{{ loadError }}</v-alert>

    <v-skeleton-loader v-else-if="isLoading" type="article" />

    <template v-else-if="content">
      <img v-if="content.orgLogoUrl" :src="content.orgLogoUrl" alt="" class="mb-4 org-logo" />

      <v-card class="mb-6">
        <v-card-item>
          <template #title>{{ content.gigName }}</template>
          <template #subtitle>{{ content.clientName }}<span v-if="content.gigDate"> — {{ formatDate(content.gigDate) }}</span></template>
        </v-card-item>
        <v-card-text>
          <v-table density="compact">
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Unit</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in content.lineItems" :key="item.id">
                <td>{{ item.description }}</td>
                <td class="text-right">{{ item.quantity }}</td>
                <td class="text-right">${{ item.unitCost.toLocaleString() }}</td>
                <td class="text-right">${{ item.lineTotal.toLocaleString() }}</td>
              </tr>
            </tbody>
          </v-table>
          <div class="text-right mt-3">
            <p class="text-body2">Subtotal: ${{ content.subtotal.toLocaleString() }}</p>
            <p v-if="content.taxRate" class="text-body2">Tax ({{ content.taxRate }}%)</p>
            <p class="text-h6">Total: ${{ content.total.toLocaleString() }}</p>
          </div>
        </v-card-text>
      </v-card>

      <!-- The one deliberate v-html spot in this app — sanitized immediately
           before rendering (DOMPurify), same as it was sanitized on save in
           Settings.vue. Never treat this as a precedent to reuse elsewhere. -->
      <v-card v-if="sanitizedTerms" class="mb-6">
        <v-card-item><template #title>Terms</template></v-card-item>
        <v-card-text>
          <div class="terms-content" v-html="sanitizedTerms" />
        </v-card-text>
      </v-card>

      <v-card v-if="content.state === 'SIGNED'">
        <v-card-text class="text-center py-8">
          <v-icon color="success" size="40" class="mb-2">mdi-check-circle</v-icon>
          <p class="text-h6">Signed</p>
          <p class="text-body2 text-medium-emphasis">
            Signed by "{{ content.signatureName }}"<span v-if="content.signedAt"> on {{ formatDate(content.signedAt) }}</span>.
          </p>
          <v-btn v-if="content.downloadUrl" class="mt-3" color="primary" :href="content.downloadUrl" target="_blank" rel="noopener">
            <v-icon start size="small">mdi-download</v-icon>
            Download PDF
          </v-btn>
        </v-card-text>
      </v-card>

      <v-card v-else>
        <v-card-item><template #title>Sign to confirm</template></v-card-item>
        <v-card-text>
          <v-alert v-if="signError" type="error" variant="tonal" class="mb-3">{{ signError }}</v-alert>
          <p class="text-body2 text-medium-emphasis mb-3">
            Type your full name and draw your signature below to confirm you agree to the terms and
            pricing above.
          </p>
          <v-text-field v-model="signatureName" label="Full name" density="compact" class="mb-2" />

          <p class="text-caption text-medium-emphasis mb-1">Signature</p>
          <div class="signature-pad-wrapper mb-1">
            <canvas ref="signatureCanvas" class="signature-canvas"></canvas>
          </div>
          <v-btn size="small" variant="text" class="mb-3" @click="clearSignature">Clear</v-btn>

          <div>
            <v-btn
              color="primary"
              :loading="isSigning"
              :disabled="!signatureName.trim() || isSignatureEmpty"
              @click="handleSign"
            >
              Sign Contract
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import DOMPurify from 'dompurify';
import SignaturePad from 'signature_pad';
import { functionsBaseUrl } from '@gigdriver/client';
import type { QuoteLineItem } from '@gigdriver/domain';

interface ContractContent {
  clientName: string;
  gigName: string;
  gigDate: string | null;
  orgLogoUrl: string | null;
  lineItems: QuoteLineItem[];
  subtotal: number;
  taxRate: number | null;
  total: number;
  termsHtml: string | null;
  state: 'DRAFT' | 'SENT' | 'SIGNED' | 'ARCHIVED';
  signatureName: string | null;
  signedAt: string | null;
  downloadUrl: string | null;
}

const route = useRoute();
const org = () => (route.query.org as string) ?? '';
const gig = () => (route.query.gig as string) ?? '';
const contractId = () => (route.query.contract as string) ?? '';
const token = () => (route.query.token as string) ?? '';

const isLoading = ref(true);
const loadError = ref<string | null>(null);
const content = ref<ContractContent | null>(null);

const sanitizedTerms = computed(() => (content.value?.termsHtml ? DOMPurify.sanitize(content.value.termsHtml) : ''));

function formatDate(value: string | Date): string {
  return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

async function loadContract() {
  if (!org() || !gig() || !contractId() || !token()) {
    loadError.value = 'This link is missing required information.';
    isLoading.value = false;
    return;
  }
  try {
    const params = new URLSearchParams({ org: org(), gig: gig(), contract: contractId(), token: token() });
    const res = await fetch(`${functionsBaseUrl()}/getContractForSigning?${params.toString()}`);
    if (!res.ok) {
      loadError.value = res.status === 404 ? 'This link is invalid or has expired.' : 'Failed to load this contract.';
      return;
    }
    content.value = (await res.json()) as ContractContent;
  } catch {
    loadError.value = 'Failed to load this contract. Check your connection and try again.';
  } finally {
    isLoading.value = false;
  }
}

const signatureName = ref('');
const isSigning = ref(false);
const signError = ref<string | null>(null);

// Drawn signature capture — the canvas only exists in the DOM once content
// has loaded and state isn't already SIGNED (v-else branch below), so the
// pad is created lazily by the watcher instead of onMounted.
const signatureCanvas = ref<HTMLCanvasElement | null>(null);
const isSignatureEmpty = ref(true);
let signaturePad: SignaturePad | null = null;

function resizeSignatureCanvas() {
  const canvas = signatureCanvas.value;
  if (!canvas) return;
  // signature_pad draws crisply only if the canvas's backing bitmap matches
  // the device pixel ratio — without this it renders blurry/offset on any
  // HiDPI screen, which is most phones and modern laptops.
  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  canvas.width = canvas.offsetWidth * ratio;
  canvas.height = canvas.offsetHeight * ratio;
  canvas.getContext('2d')?.scale(ratio, ratio);
  signaturePad?.clear();
  isSignatureEmpty.value = true;
}

function setupSignaturePad() {
  const canvas = signatureCanvas.value;
  if (!canvas || signaturePad) return;
  signaturePad = new SignaturePad(canvas);
  signaturePad.addEventListener('endStroke', () => {
    isSignatureEmpty.value = signaturePad?.isEmpty() ?? true;
  });
  resizeSignatureCanvas();
  window.addEventListener('resize', resizeSignatureCanvas);
}

function clearSignature() {
  signaturePad?.clear();
  isSignatureEmpty.value = true;
}

watch(
  () => content.value?.state,
  async state => {
    if (content.value && state !== 'SIGNED') {
      await nextTick();
      setupSignaturePad();
    }
  },
);

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeSignatureCanvas);
  signaturePad?.off();
});

async function handleSign() {
  if (!signatureName.value.trim() || isSignatureEmpty.value || !signaturePad) return;
  try {
    isSigning.value = true;
    signError.value = null;
    const res = await fetch(`${functionsBaseUrl()}/signContract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        org: org(),
        gig: gig(),
        contract: contractId(),
        token: token(),
        signatureName: signatureName.value.trim(),
        signatureImageDataUrl: signaturePad.toDataURL('image/png'),
      }),
    });
    if (res.status === 409) {
      // Someone else (or another tab) already signed — reload to show the
      // read-only "already signed" view rather than erroring.
      await loadContract();
      return;
    }
    if (!res.ok) {
      signError.value = 'Failed to sign this contract. Please try again.';
      return;
    }
    await loadContract();
  } catch {
    signError.value = 'Failed to sign this contract. Check your connection and try again.';
  } finally {
    isSigning.value = false;
  }
}

onMounted(loadContract);
</script>

<style scoped>
.terms-content :deep(p) {
  margin: 0 0 0.75em;
}
.terms-content :deep(ul),
.terms-content :deep(ol) {
  margin: 0 0 0.75em;
  padding-left: 1.5em;
}
.org-logo {
  max-height: 64px;
  max-width: 220px;
  object-fit: contain;
}
.signature-pad-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  background: #fff;
}
.signature-canvas {
  width: 100%;
  height: 150px;
  display: block;
  touch-action: none;
}
</style>
